import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

export type ActivityState = {
  typewriting: boolean;
  registerTypewriting: (messageId: string) => () => void;
  isMessageTypewriting: (messageId: string) => boolean;
};

const ConversationActivityIndicatorContext = createContext<ActivityState>({
  typewriting: false,
  registerTypewriting: () => {
    return () => undefined;
  },
  isMessageTypewriting: () => false,
});

type Props = {
  children: ReactNode;
};

export function ConversationActivityIndicatorProvider({children}: Props) {
  const [typewritingMessageId, setTypewritingMessageId] = useState<
    string | null
  >(null);

  const registerTypewriting = useCallback((messageId: string) => {
    setTypewritingMessageId(messageId);

    return () =>
      setTypewritingMessageId((currentMessageId) =>
        currentMessageId === messageId ? null : currentMessageId,
      );
  }, []);

  const isMessageTypewriting = useCallback(
    (messageId: string) => typewritingMessageId === messageId,
    [typewritingMessageId],
  );
  const typewriting = typewritingMessageId !== null;

  const value = useMemo(
    () => ({
      typewriting,
      registerTypewriting,
      isMessageTypewriting,
    }),
    [isMessageTypewriting, registerTypewriting, typewriting],
  );

  return (
    <ConversationActivityIndicatorContext.Provider value={value}>
      {children}
    </ConversationActivityIndicatorContext.Provider>
  );
}

export function useConversationTypewriting() {
  return useContext(ConversationActivityIndicatorContext);
}
